import {FormControl, Grid, MenuItem, Select, TextField, Typography, makeStyles} from '@material-ui/core';
import React from 'react';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {ContactFormStore} from '../../ContactForm/ContactFormStore/ContactFormStore';
import {observer} from 'mobx-react-lite';
import InputSsn from '../../../UI/EmcApplicationQuestions/inputs/core/InputSsn';
import InputPhone from '../../../UI/EmcApplicationQuestions/inputs/core/InputPhone';
import FixedNavigation from './common/FixedNavigation';

interface VerifyIdentityProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    quoteResponse: any[];
    onExit: () => void;
    onNext: () => void;
}
const calculateAge = (birthMonth: number, birthDay: number, birthYear: number) => {
    const today = new Date();
    const birthDate = new Date(birthYear, birthMonth - 1, birthDay); // Months are 0-based in JavaScript's Date
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
};

const useStyles = makeStyles(() => ({
    label: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '30.27px',
        color: '#838383',
    },
    selectFormControl: {
        minWidth: '100%',
    },
}));

export const VerifyIdentity: React.FC<VerifyIdentityProps> = observer(
    ({parameterStore, contactFormStore, onNext, onExit}) => {
        const {onValueChangedPreApproval, approval} = parameterStore;
        const firstName = approval.firstName.value as string;
        const lastName = approval.lastName.value as string;
        const bday = approval.bday.value as number;
        const bmonth = approval.bmonth.value as number;
        const byear = approval.byear.value as number;
        const streetAddress = approval.address.value as string;
        const state = approval.state.value as string;
        const city = approval.addressCity.value as string;
        const driversLicenseNumber = approval.driversLicenseNumber.value as string;
        const dlState = approval.driversLicenseState.value as string;
        const validDriversLicense = approval.validDriversLicense.value as string;
        const zip = approval.addressZipCode.value as string;
        const socialSecurity = approval.socialSecurityNumber.value as string;
        const age = calculateAge(bmonth, bday, byear);

        const onNextClick = () => {
            contactFormStore.saveForm();
            onNext();
        };

        const classes = useStyles();

        return (
            <div style={{marginTop: 20}}>
                <Typography variant="h4">Let’s Verify Your Identity</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Review</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="First Name"
                            value={firstName}
                            onChange={(e) => onValueChangedPreApproval(approval.firstName, e.target.value)}
                            fullWidth
                            variant="outlined"
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Last Name"
                            value={lastName}
                            onChange={(e) => onValueChangedPreApproval(approval.lastName, e.target.value)}
                            fullWidth
                            variant="outlined"
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2}>
                        <TextField
                            label="Birth Month"
                            value={bmonth}
                            onChange={(e) => onValueChangedPreApproval(approval.bmonth, e.target.value)}
                            fullWidth
                            variant="outlined"
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2}>
                        <TextField
                            label="Birth Day"
                            value={bday}
                            onChange={(e) => onValueChangedPreApproval(approval.bday, e.target.value)}
                            fullWidth
                            variant="outlined"
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2}>
                        <TextField
                            label="Birth Year"
                            value={byear}
                            onChange={(e) => onValueChangedPreApproval(approval.byear, e.target.value)}
                            fullWidth
                            variant="outlined"
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={1} md={2}>
                        <TextField label="Age" value={age !== null ? age : '?'} fullWidth variant="outlined" disabled />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <InputPhone
                            label="Phone Number"
                            value={approval.phoneNumber.value ?? ''}
                            fullWidth
                            onChange={(value: string) => onValueChangedPreApproval(approval.phoneNumber, value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6">Input</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography className={classes.label}>Street Address</Typography>
                        <TextField
                            value={streetAddress}
                            onChange={(e) => onValueChangedPreApproval(approval.address, e.target.value)}
                            fullWidth
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography className={classes.label}>City</Typography>
                        <TextField
                            value={city}
                            onChange={(e) => onValueChangedPreApproval(approval.addressCity, e.target.value)}
                            fullWidth
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography className={classes.label}>State</Typography>
                        <FormControl variant="outlined" className={classes.selectFormControl}>
                            <Select
                                aria-label="State"
                                variant="outlined"
                                value={state}
                                onChange={(event) => onValueChangedPreApproval(approval.state, event.target.value)}
                                error={!approval.state.value}
                                placeholder="AK"
                            >
                                <MenuItem value="AK">AK</MenuItem>
                                <MenuItem value="AL">AL</MenuItem>
                                <MenuItem value="AR">AR</MenuItem>
                                <MenuItem value="AZ">AZ</MenuItem>
                                <MenuItem value="CA">CA</MenuItem>
                                <MenuItem value="CO">CO</MenuItem>
                                <MenuItem value="CT">CT</MenuItem>
                                <MenuItem value="DC">DC</MenuItem>
                                <MenuItem value="DE">DE</MenuItem>
                                <MenuItem value="FL">FL</MenuItem>
                                <MenuItem value="GA">GA</MenuItem>
                                <MenuItem value="HI">HI</MenuItem>
                                <MenuItem value="IA">IA</MenuItem>
                                <MenuItem value="ID">ID</MenuItem>
                                <MenuItem value="IL">IL</MenuItem>
                                <MenuItem value="IN">IN</MenuItem>
                                <MenuItem value="KS">KS</MenuItem>
                                <MenuItem value="KY">KY</MenuItem>
                                <MenuItem value="LA">LA</MenuItem>
                                <MenuItem value="MA">MA</MenuItem>
                                <MenuItem value="MD">MD</MenuItem>
                                <MenuItem value="ME">ME</MenuItem>
                                <MenuItem value="MI">MI</MenuItem>
                                <MenuItem value="MN">MN</MenuItem>
                                <MenuItem value="MO">MO</MenuItem>
                                <MenuItem value="MS">MS</MenuItem>
                                <MenuItem value="MT">MT</MenuItem>
                                <MenuItem value="NC">NC</MenuItem>
                                <MenuItem value="ND">ND</MenuItem>
                                <MenuItem value="NE">NE</MenuItem>
                                <MenuItem value="NH">NH</MenuItem>
                                <MenuItem value="NJ">NJ</MenuItem>
                                <MenuItem value="NM">NM</MenuItem>
                                <MenuItem value="NV">NV</MenuItem>
                                <MenuItem value="NY">NY</MenuItem>
                                <MenuItem value="OH">OH</MenuItem>
                                <MenuItem value="OK">OK</MenuItem>
                                <MenuItem value="OR">OR</MenuItem>
                                <MenuItem value="PA">PA</MenuItem>
                                <MenuItem value="RI">RI</MenuItem>
                                <MenuItem value="SC">SC</MenuItem>
                                <MenuItem value="SD">SD</MenuItem>
                                <MenuItem value="TN">TN</MenuItem>
                                <MenuItem value="TX">TX</MenuItem>
                                <MenuItem value="UT">UT</MenuItem>
                                <MenuItem value="VA">VA</MenuItem>
                                <MenuItem value="VT">VT</MenuItem>
                                <MenuItem value="WA">WA</MenuItem>
                                <MenuItem value="WI">WI</MenuItem>
                                <MenuItem value="WV">WV</MenuItem>
                                <MenuItem value="WY">WY</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography className={classes.label}>Zipcode</Typography>
                        <TextField
                            value={zip}
                            onChange={(e) => onValueChangedPreApproval(approval.addressZipCode, e.target.value)}
                            fullWidth
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography className={classes.label}>Social Security Number</Typography>
                        <InputSsn
                            aria-label="Social Security Number"
                            value={socialSecurity}
                            onChange={(value: string) =>
                                onValueChangedPreApproval(approval.socialSecurityNumber, value)
                            }
                            error={approval.socialSecurityNumber.value?.length !== 9}
                            showHideButton
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}></Grid>
                    <Grid item sm={12} md={3} lg={2}>
                        <Typography className={classes.label}>DL Status</Typography>
                        <FormControl variant="outlined" className={classes.selectFormControl}>
                            <Select
                                aria-label="DL Status"
                                variant="outlined"
                                value={validDriversLicense}
                                onChange={(event) =>
                                    onValueChangedPreApproval(approval.validDriversLicense, event.target.value)
                                }
                                error={!approval.validDriversLicense.value}
                                placeholder="Yes"
                            >
                                <MenuItem value="Yes">Yes</MenuItem>
                                <MenuItem value="State ID">State ID</MenuItem>
                                <MenuItem value="No ID - Applicant is a Minor">No ID - Applicant is a Minor</MenuItem>
                                <MenuItem value="No ID">No ID</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item sm={12} md={3} lg={2}>
                        <Typography className={classes.label}>DL State</Typography>
                        <FormControl
                            variant="outlined"
                            className={classes.selectFormControl}
                            disabled={validDriversLicense === 'Yes' ? false : true}
                        >
                            <Select
                                aria-label="DL State"
                                variant="outlined"
                                value={dlState}
                                onChange={(event) =>
                                    onValueChangedPreApproval(approval.driversLicenseState, event.target.value)
                                }
                                error={validDriversLicense === 'Yes' && !approval.driversLicenseState.value}
                                placeholder="AK"
                            >
                                <MenuItem value="AK">AK</MenuItem>
                                <MenuItem value="AL">AL</MenuItem>
                                <MenuItem value="AR">AR</MenuItem>
                                <MenuItem value="AZ">AZ</MenuItem>
                                <MenuItem value="CA">CA</MenuItem>
                                <MenuItem value="CO">CO</MenuItem>
                                <MenuItem value="CT">CT</MenuItem>
                                <MenuItem value="DC">DC</MenuItem>
                                <MenuItem value="DE">DE</MenuItem>
                                <MenuItem value="FL">FL</MenuItem>
                                <MenuItem value="GA">GA</MenuItem>
                                <MenuItem value="HI">HI</MenuItem>
                                <MenuItem value="IA">IA</MenuItem>
                                <MenuItem value="ID">ID</MenuItem>
                                <MenuItem value="IL">IL</MenuItem>
                                <MenuItem value="IN">IN</MenuItem>
                                <MenuItem value="KS">KS</MenuItem>
                                <MenuItem value="KY">KY</MenuItem>
                                <MenuItem value="LA">LA</MenuItem>
                                <MenuItem value="MA">MA</MenuItem>
                                <MenuItem value="MD">MD</MenuItem>
                                <MenuItem value="ME">ME</MenuItem>
                                <MenuItem value="MI">MI</MenuItem>
                                <MenuItem value="MN">MN</MenuItem>
                                <MenuItem value="MO">MO</MenuItem>
                                <MenuItem value="MS">MS</MenuItem>
                                <MenuItem value="MT">MT</MenuItem>
                                <MenuItem value="NC">NC</MenuItem>
                                <MenuItem value="ND">ND</MenuItem>
                                <MenuItem value="NE">NE</MenuItem>
                                <MenuItem value="NH">NH</MenuItem>
                                <MenuItem value="NJ">NJ</MenuItem>
                                <MenuItem value="NM">NM</MenuItem>
                                <MenuItem value="NV">NV</MenuItem>
                                <MenuItem value="NY">NY</MenuItem>
                                <MenuItem value="OH">OH</MenuItem>
                                <MenuItem value="OK">OK</MenuItem>
                                <MenuItem value="OR">OR</MenuItem>
                                <MenuItem value="PA">PA</MenuItem>
                                <MenuItem value="RI">RI</MenuItem>
                                <MenuItem value="SC">SC</MenuItem>
                                <MenuItem value="SD">SD</MenuItem>
                                <MenuItem value="TN">TN</MenuItem>
                                <MenuItem value="TX">TX</MenuItem>
                                <MenuItem value="UT">UT</MenuItem>
                                <MenuItem value="VA">VA</MenuItem>
                                <MenuItem value="VT">VT</MenuItem>
                                <MenuItem value="WA">WA</MenuItem>
                                <MenuItem value="WI">WI</MenuItem>
                                <MenuItem value="WV">WV</MenuItem>
                                <MenuItem value="WY">WY</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={8}>
                        <Typography className={classes.label}>DL Number</Typography>
                        <TextField
                            value={driversLicenseNumber}
                            onChange={(e) => onValueChangedPreApproval(approval.driversLicenseNumber, e.target.value)}
                            fullWidth
                            variant="outlined"
                            disabled={validDriversLicense === 'Yes' ? false : true}
                            error={validDriversLicense === 'Yes' && !approval.driversLicenseNumber.value}
                        />
                    </Grid>
                </Grid>
                <FixedNavigation
                    isNextDisabled={
                        approval.socialSecurityNumber.value?.length !== 9 ||
                        !validDriversLicense ||
                        (validDriversLicense === 'Yes' &&
                            (!approval.driversLicenseState.value || !approval.driversLicenseNumber.value))
                    }
                    isLoading={false}
                    onNext={onNextClick}
                />
            </div>
        );
    },
);
